import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

const ProgressStepNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const steps = [
    {
      id: 'contact-info',
      path: '/',
      label: 'Informations de contact',
      shortLabel: 'Contact',
      icon: 'User',
      description: 'Renseignez vos informations personnelles'
    },
    {
      id: 'document-upload',
      path: '/document',
      label: 'Téléchargement du document',
      shortLabel: 'Document',
      icon: 'Upload',
      description: 'Téléchargez votre pièce d\'identité'
    },
    {
      id: 'success',
      path: '/submission',
      label: 'Confirmation de soumission',
      shortLabel: 'Confirmation',
      icon: 'CheckCircle2',
      description: 'Votre demande a été soumise avec succès'
    }
  ];

  const getCurrentStepIndex = () => {
    return steps.findIndex(step => step.path === location.pathname);
  };

  const currentStepIndex = getCurrentStepIndex();
  const completedSteps = currentStepIndex;

  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStepIndex) return 'completed';
    if (stepIndex === currentStepIndex) return 'current';
    return 'upcoming';
  };

  const handleStepClick = (step, stepIndex) => {
    // Only allow navigation to completed steps or current step
    if (stepIndex <= currentStepIndex) {
      navigate(step.path);
    }
  };

  const getStepClasses = (stepIndex) => {
    const status = getStepStatus(stepIndex);
    const baseClasses = "flex items-center justify-center w-10 h-10 rounded-full border-2 transition-smooth";
    
    switch (status) {
      case 'completed':
        return `${baseClasses} bg-success border-success text-success-foreground cursor-pointer hover:bg-success/90`;
      case 'current':
        return `${baseClasses} bg-primary border-primary text-primary-foreground`;
      case 'upcoming':
        return `${baseClasses} bg-surface border-border text-text-muted`;
      default:
        return baseClasses;
    }
  };

  const getConnectorClasses = (stepIndex) => {
    const isCompleted = stepIndex < currentStepIndex;
    return `flex-1 h-0.5 mx-4 transition-smooth ${
      isCompleted ? 'bg-success' : 'bg-border'
    }`;
  };

  const ProgressBar = () => {
    const progressPercentage = ((currentStepIndex + 1) / steps.length) * 100;
    
    return (
      <div className="w-full bg-border rounded-full h-2 mb-4">
        <div 
          className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
    );
  };

  return (
    <nav className="top-16 z-40 bg-surface border-b border-border shadow-elevation-1 mb-10" aria-label="Progression de la vérification">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Mobile Progress Bar */}
        <div className="block md:hidden mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-text-secondary font-inter">
              Étape {currentStepIndex + 1} sur {steps.length - 1}
            </span>
            <span className="text-sm font-medium text-primary font-inter">
              {Math.round(((currentStepIndex + 1) / (steps.length - 1)) * 100)}%
            </span>
          </div>
          <ProgressBar />
          <div className="text-center">
            <h2 className="text-lg font-semibold text-text-primary font-inter">
              {steps[currentStepIndex]?.label}
            </h2>
            <p className="text-sm text-text-secondary font-inter mt-1">
              {steps[currentStepIndex]?.description}
            </p>
          </div>
        </div>

        {/* Desktop Step Navigation */}
        <div className="hidden md:block">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  {/* Step Circle */}
                  <button
                    onClick={() => handleStepClick(step, index)}
                    className={`${getStepClasses(index)} focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                    disabled={index > currentStepIndex}
                    aria-label={`${step.label} - ${getStepStatus(index) === 'completed' ? 'Terminé' : getStepStatus(index) === 'current' ? 'En cours' : 'À venir'}`}
                  >
                    {getStepStatus(index) === 'completed' ? (
                      <Icon name="Check" size={20} strokeWidth={2.5} />
                    ) : (
                      <Icon name={step.icon} size={20} strokeWidth={2} />
                    )}
                  </button>

                  {/* Step Label */}
                  <div className="mt-3 text-center max-w-32">
                    <p className={`text-sm font-medium font-inter ${
                      getStepStatus(index) === 'current' ?'text-primary' 
                        : getStepStatus(index) === 'completed' ?'text-success' :'text-text-muted'
                    }`}>
                      {step.shortLabel}
                    </p>
                    <p className="text-xs text-text-secondary font-inter mt-1 leading-tight">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className={getConnectorClasses(index)} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Progress Summary */}
        <div className="hidden md:flex items-center justify-center mt-6 pt-4 border-t border-border-light">
          <div className="flex items-center space-x-4 text-sm text-text-secondary font-inter">
            <span className="flex items-center space-x-1">
              <Icon name="CheckCircle" size={16} color="#10B981" />
              <span>{completedSteps} terminé{completedSteps > 1 ? 's' : ''}</span>
            </span>
            <span className="w-1 h-1 bg-border rounded-full" />
            <span className="flex items-center space-x-1">
              <Icon name="Clock" size={16} color="#6B7280" />
              <span>{steps.length - currentStepIndex - 1} restant{steps.length - currentStepIndex - 1 > 1 ? 's' : ''}</span>
            </span>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default ProgressStepNavigation;